import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getNextProfileLocationState } from '../components/utils/profileRouteState';

export enum ProfileSubview {
  PriceEstimations = 'priceEstimations',
  SellListings = 'sellListings',
  BuyListings = 'buyListings',
  NotificationSettings = 'notificationSettings',
  AreaInterests = 'areaInterests',
}

interface ProfileNavigationContextType {
  subview: ProfileSubview | null;
  isProfileRoute: boolean;
  estimationIdFromUrl?: string;
  openSubview: (subview: ProfileSubview) => void;
  closeSubview: () => void;
  openEstimationDetail: (id: string) => void;
  closeEstimationDetail: () => void;
}

const ProfileNavigationContext = createContext<ProfileNavigationContextType>({
  subview: null,
  isProfileRoute: false,
  openSubview: () => {},
  closeSubview: () => {},
  openEstimationDetail: () => {},
  closeEstimationDetail: () => {},
});

export const useProfileNavigation = () => useContext(ProfileNavigationContext);

const subviewRoutes: Record<string, ProfileSubview> = {
  '/profile/price-estimations': ProfileSubview.PriceEstimations,
  '/profile/sell-listings': ProfileSubview.SellListings,
  '/profile/buy-listings': ProfileSubview.BuyListings,
  '/profile/notification-settings': ProfileSubview.NotificationSettings,
  '/profile/area-interests': ProfileSubview.AreaInterests,
};

const subviewPaths: Record<ProfileSubview, string> = {
  [ProfileSubview.PriceEstimations]: '/profile/price-estimations',
  [ProfileSubview.SellListings]: '/profile/sell-listings',
  [ProfileSubview.BuyListings]: '/profile/buy-listings',
  [ProfileSubview.NotificationSettings]: '/profile/notification-settings',
  [ProfileSubview.AreaInterests]: '/profile/area-interests',
};

export const ProfileNavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const history = useHistory();
  const { pathname, state } = useLocation();

  const value = useMemo(() => {
    const nextProfileLocationState = getNextProfileLocationState(state);
    const matched = Object.entries(subviewRoutes).find(
      ([route]) => pathname === route || pathname.startsWith(route + '/')
    );
    const subview: ProfileSubview | null = matched?.[1] ?? null;
    const isProfileRoute = pathname === '/profile' || !!matched;

    const estimationMatch = pathname.match(/^\/profile\/price-estimations\/([^/]+)$/);
    const estimationIdFromUrl = estimationMatch
      ? decodeURIComponent(estimationMatch[1])
      : undefined;

    return {
      subview,
      isProfileRoute,
      estimationIdFromUrl,
      openSubview: (sv: ProfileSubview) => {
        history.push(subviewPaths[sv], nextProfileLocationState);
      },
      closeSubview: () => history.push('/profile', nextProfileLocationState),
      openEstimationDetail: (id: string) =>
        history.push(
          `/profile/price-estimations/${encodeURIComponent(id)}`,
          nextProfileLocationState,
        ),
      closeEstimationDetail: () =>
        history.push('/profile/price-estimations', nextProfileLocationState),
    };
  }, [pathname, state, history]);

  return (
    <ProfileNavigationContext.Provider value={value}>
      {children}
    </ProfileNavigationContext.Provider>
  );
};
